import { ProjectSubtype, ProjectType } from "@/domain/project/constants"
import { PropertyTypes } from "@/domain/property/constants"
import type { EntityLocked, SHARED_DATA_TYPE_VALUE } from "@/domain/sharedData/constants"
import type { InterBuPostInterBuInputJsonld } from "@/networking/api"
import { isNotNullNorUndefined } from "@/utils/ts-utils"
import { createContext, useContext, useState } from "react"

const contextDefaultValues: InterBuPostInterBuInputJsonld = {
  people_id: "" as string,
  type: ProjectSubtype.REAL_ESTATE as string, // ProjectType.OFFER as ProjectType,
  subtype: ProjectType.OFFER as ProjectType, // ProjectSubtype.REAL_ESTATE as ProjectSubtype,
  property_type: PropertyTypes.HOUSE as PropertyTypes,
  subject: "InterBU" as string,
  message: "" as string,
  price_unit: "eur" as string,
  surface_unit: "sqm" as string,
  from_cluster_id: "" as string,
  clusters: [] as string[],
  people_comment: null,
  price_min: null,
  price_max: null,
  surface_min: null,
  surface_max: null,
  address_1: null,
  address_2: null,
  address_3: null,
  zipcode: null,
  country: null,
  city: null,
  project_comment: null,
  project_maturity: null,
}

type ShareEntity = EntityLocked & { type: SHARED_DATA_TYPE_VALUE }
type OptionValueBase = { id: string }
type SimpleOption<OV extends OptionValueBase> = { label: string; value: OV }
type SearchTerms = Record<string, string>
// Coordinates of the geocoded property address: kept out of formState so they are never posted
// (ADR 0004 D2); they only drive the nearby agency suggestions.
export type AddressLocation = { lat: number; lng: number }

type CreationMultiStepFormContext = {
  formState: InterBuPostInterBuInputJsonld
  setFormState: React.Dispatch<React.SetStateAction<InterBuPostInterBuInputJsonld>>
  sharedEntity?: ShareEntity
  setSharedEntity: React.Dispatch<React.SetStateAction<ShareEntity | undefined>>
  storeValues: { [k: string]: unknown[] }
  setValuesSelected: <V extends OptionValueBase, T extends SimpleOption<V>>(
    fieldname: string,
    selectedOptions?: T[] | null
  ) => void
  setValueSelected: <V extends OptionValueBase, T extends SimpleOption<V>>(
    fieldname: string,
    selectedOptions?: T | null
  ) => void
  storeSearchTerms: SearchTerms
  setSearchTerm: (fieldname: string, search: string) => void
  addressLocation: AddressLocation | null
  setAddressLocation: React.Dispatch<React.SetStateAction<AddressLocation | null>>
}

const formDefaultValues: CreationMultiStepFormContext = {
  formState: contextDefaultValues,
  setFormState: () => {},
  sharedEntity: undefined,
  setSharedEntity: () => {},
  storeValues: {},
  setValuesSelected: () => {},
  setValueSelected: () => {},
  storeSearchTerms: {},
  setSearchTerm: () => {},
  addressLocation: null,
  setAddressLocation: () => {},
}

const creationMultiStepFormContext = createContext(formDefaultValues)

export const CreationMultiStepFormProvider: React.FC<
  React.PropsWithChildren<{ sharedEntity?: ShareEntity }>
> = ({ children, sharedEntity }) => {
  const [memSharedEntity, setMemSharedEntity] = useState(sharedEntity)

  const [storeValues, setStoreValues] = useState<{ [k: string]: OptionValueBase[] }>({})
  const [storeSearchTerms, setStoreSearchTerms] = useState<SearchTerms>({})
  const [addressLocation, setAddressLocation] = useState<AddressLocation | null>(null)

  // Set
  const setValuesSelected = <K extends OptionValueBase, T extends SimpleOption<K>>(
    fieldname: string,
    selectedOptions?: T[] | null
  ) => {
    const options = { ...storeValues }
    options[fieldname] = (selectedOptions ?? []).map(opt => opt.value)
    // console.log("set multi", fieldname, options)
    setStoreValues(options)
  }
  const setValueSelected = <K extends OptionValueBase, T extends SimpleOption<K>>(
    fieldname: string,
    selectedOption?: T | null
  ) => {
    const options = { ...storeValues }
    options[fieldname] = isNotNullNorUndefined(selectedOption) ? [selectedOption.value] : []
    // console.log("set", fieldname, options)
    setStoreValues(options)
  }
  const setSearchTerm = (fieldname: string, search: string) => {
    const searchTerms = { ...storeSearchTerms }
    searchTerms[fieldname] = search ?? ""
    setStoreSearchTerms(searchTerms)
  }

  // FormState
  const [formState, setFormState] = useState({
    ...contextDefaultValues,
    people_id: sharedEntity?.id ?? "",
  })

  return (
    <creationMultiStepFormContext.Provider
      value={{
        formState,
        setFormState,
        sharedEntity: memSharedEntity,
        setSharedEntity: setMemSharedEntity,
        // setStoreValues,
        // mergeValues,
        storeValues,
        setValuesSelected,
        setValueSelected,
        storeSearchTerms,
        setSearchTerm,
        addressLocation,
        setAddressLocation,
        // getValues,
        // getValue,
      }}
    >
      {children}
    </creationMultiStepFormContext.Provider>
  )
}

export const useCreationMultiStepFormContext = () => useContext(creationMultiStepFormContext)
